import { getFirestore, collection, addDoc, doc, updateDoc, query, where, orderBy, limit, startAfter, getDocs, getCountFromServer, writeBatch, Timestamp, } from "firebase/firestore";
import { PageCursor } from "../../domain/models/pagination";
import { mapFirebaseError } from "./errorMapper";
import { contactMessageDTOtoDomain } from "./mappers";
export class FirebaseContactRepository {
    db = getFirestore();
    coll = collection(this.db, "contact_messages");
    async sendMessage(message) {
        try {
            await addDoc(this.coll, {
                userId: message.userId,
                userName: message.userName,
                userEmail: message.userEmail,
                title: message.title,
                body: message.body,
                preferredContact: message.preferredContact,
                contactValue: message.contactValue,
                createdAt: Timestamp.now(),
                status: "new",
            });
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async fetchPage(pageSize, after) {
        try {
            let q = query(this.coll, orderBy("createdAt", "desc"), limit(pageSize));
            if (after?.backing) {
                q = query(q, startAfter(after.backing));
            }
            const snap = await getDocs(q);
            const items = snap.docs.map((d) => ({ id: d.id, ...contactMessageDTOtoDomain(d.id, d.data()) }));
            const nextCursor = snap.docs.length === pageSize ? new PageCursor(snap.docs[snap.docs.length - 1]) : null;
            return { items, nextCursor };
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async updateStatus(id, status) {
        try {
            await updateDoc(doc(this.db, "contact_messages", id), { status });
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async countByStatus() {
        try {
            const [newSnap, readSnap, resolvedSnap] = await Promise.all([
                getCountFromServer(query(this.coll, where("status", "==", "new"))),
                getCountFromServer(query(this.coll, where("status", "==", "read"))),
                getCountFromServer(query(this.coll, where("status", "==", "resolved"))),
            ]);
            return { new: newSnap.data().count, read: readSnap.data().count, resolved: resolvedSnap.data().count };
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    /** Repeatedly batches over `q` applying `apply` to each doc, 500 ops per commit, until exhausted. */
    async batchOver(q, apply) {
        for (;;) {
            const snap = await getDocs(query(q, limit(450)));
            if (snap.empty)
                return;
            const batch = writeBatch(this.db);
            snap.docs.forEach((d) => apply(batch, d.id));
            await batch.commit();
            if (snap.docs.length < 450)
                return;
        }
    }
    async markAllAsRead() {
        try {
            await this.batchOver(query(this.coll, where("status", "==", "new")), (batch, id) => batch.update(doc(this.db, "contact_messages", id), { status: "read" }));
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async closeAll() {
        try {
            await this.batchOver(query(this.coll, where("status", "in", ["new", "read"])), (batch, id) => batch.update(doc(this.db, "contact_messages", id), { status: "resolved" }));
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async deleteAll() {
        try {
            await this.batchOver(query(this.coll), (batch, id) => batch.delete(doc(this.db, "contact_messages", id)));
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
}
//# sourceMappingURL=FirebaseContactRepository.js.map